/**
 * Phone Number Formatting Utility
 * Provides real-time formatting for phone numbers with support for international formats
 *
 * Supported formats:
 * - Indian: +91-98765-43210 or 98765 43210
 * - US: +1 (555) 123-4567
 * - Generic: +CC-XXXX-XXXX or similar
 */

/**
 * Detects the phone number format based on country code or pattern
 * @param {string} value - The phone number string (partial or complete)
 * @returns {string} - Detected country code or 'default'
 */
const detectPhoneFormat = (value) => {
  if (!value) return 'default';

  // Check if starts with +91 (India)
  if (value.startsWith('+91')) return 'IN';

  // All digit input goes to Indian format (will limit to 10 digits)
  if (/^\d/.test(value)) {
    return 'IN';
  }

  return 'default';
};

/**
 * Formats Indian phone numbers (+91)
 * Format: +91 xxxxx xxxxx (country code + 5 digits + 5 digits)
 * Automatically adds +91 if user enters just 10 digits
 * @param {string} value - The phone number (digits and + only)
 * @returns {string} - Formatted phone number
 */
const formatIndianPhone = (value) => {
  // Remove all non-digit characters
  let cleaned = value.replace(/\D/g, '');

  // Limit to maximum 10 digits (will auto-add +91 prefix)
  cleaned = cleaned.slice(0, 10);

  // Auto-add country code (91) when user has entered 10 digits
  if (cleaned.length === 10) {
    cleaned = '91' + cleaned;
  }

  if (cleaned.length === 0) return '';
  if (cleaned.length <= 2) return '+' + cleaned;
  if (cleaned.length <= 7) return '+' + cleaned.slice(0, 2) + ' ' + cleaned.slice(2);

  // Format as: +91 xxxxx xxxxx (5 digits + 5 digits after country code)
  return '+' + cleaned.slice(0, 2) + ' ' + cleaned.slice(2, 7) + ' ' + cleaned.slice(7, 12);
};


/**
 * Main phone formatter function
 * Formats all phone input as Indian format (+91)
 * Only accepts 10 digits per user requirement
 * @param {string} value - The raw phone number input (10 digits only)
 * @returns {string} - Formatted phone number as +91 XXXXX XXXXX
 */
export const formatPhoneNumber = (value) => {
  if (!value || typeof value !== 'string') return '';

  return formatIndianPhone(value);
};

/**
 * Validates a phone number
 * Checks minimum length and valid characters
 * @param {string} value - The phone number to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidPhoneNumber = (value) => {
  if (!value || typeof value !== 'string') return false;

  // Remove all non-digit and non-plus characters
  const digits = value.replace(/\D/g, '');

  // Valid if at least 7 digits (minimum phone number length)
  return digits.length >= 7;
};

/**
 * Validates Indian phone number format
 * Checks that phone number has exactly 10 digits and starts with 6, 7, 8, or 9
 * Provides specific diagnostic error messages for each failure case
 * @param {string} value - The phone number to validate (10 digits only)
 * @returns {object} - { isValid: boolean, error: string|null }
 */
export const validateIndianPhoneNumber = (value) => {
  // Check if value is provided and is a string
  if (!value || typeof value !== 'string') {
    return { isValid: false, error: 'Phone number is required.' };
  }

  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // Case 1: No digits entered
  if (digits.length === 0) {
    return { isValid: false, error: 'Please enter a phone number.' };
  }

  // Case 2: Too few digits
  if (digits.length < 10) {
    const remaining = 10 - digits.length;
    return {
      isValid: false,
      error: `Incomplete: You've entered ${digits.length} digit${digits.length !== 1 ? 's' : ''}. Please add ${remaining} more digit${remaining !== 1 ? 's' : ''}.`
    };
  }

  // Case 3: Too many digits
  if (digits.length > 10) {
    return {
      isValid: false,
      error: `Too long: You've entered ${digits.length} digits. Indian mobile numbers use exactly 10 digits.`
    };
  }

  // Case 4: Invalid first digit (not 6, 7, 8, or 9)
  if (!/^[6789]/.test(digits)) {
    return {
      isValid: false,
      error: `Invalid start digit: Your number starts with ${digits[0]}. Indian mobile numbers must start with 6, 7, 8, or 9.`
    };
  }

  // All validations passed
  return { isValid: true, error: null };
};

/**
 * Extracts raw digits from a formatted phone number
 * Useful for API submission
 * @param {string} value - The formatted phone number
 * @returns {string} - Raw phone number with only digits and leading +
 */
export const getPhoneNumberDigits = (value) => {
  if (!value || typeof value !== 'string') return '';

  // Remove spaces, dashes, parentheses, but keep + and digits
  return value.replace(/[^\d+]/g, '');
};

/**
 * Removes +91 prefix from phone number for editing
 * Returns only the 10 digits for user input
 * @param {string} value - The phone number with +91 prefix
 * @returns {string} - Just the 10 digits without +91
 */
export const removeCountryCode = (value) => {
  if (!value || typeof value !== 'string') return '';

  // Check if the value contains a + sign (indicating country code format)
  const hasCountryCodeMarker = value.includes('+');

  // Remove all non-digits
  const digits = value.replace(/\D/g, '');

  // Only strip 91 if:
  // 1. The value had a + sign (indicating +91 format like "+91 98765 43210")
  // 2. AND it starts with 91 AND has 12+ digits (91 + 10 digits)
  if (hasCountryCodeMarker && digits.startsWith('91') && digits.length >= 12) {
    // Skip the '91' and take the next 10 digits
    return digits.substring(2, 12);
  }

  // If exactly 10 digits (no country code), return as is
  if (digits.length === 10) {
    return digits;
  }

  // If more than 10 digits without country code, take first 10
  if (digits.length > 10) {
    return digits.substring(0, 10);
  }

  // Otherwise return as is (partial input)
  return digits;
};

export default {
  formatPhoneNumber,
  isValidPhoneNumber,
  getPhoneNumberDigits,
  removeCountryCode
};
